/**
 * Accumulates AI chat message sends per-adapter and flushes them as a single
 * feature:ai_chatbot_messages analytics event with { adapter, count }.
 *
 * Flushes on: 60s interval (lazy), beforeunload, adapter switch, and main
 * process pre-quit broadcast (see electron/main.ts).
 */

const FLUSH_INTERVAL_MS = 60_000;

type Counts = Map<string, number>;

const counts: Counts = new Map();
let flushTimer: ReturnType<typeof setInterval> | null = null;
let beforeUnloadBound = false;
let ipcListenerBound = false;

function ensureTimer(): void {
  if (flushTimer !== null) return;
  flushTimer = setInterval(() => {
    void flushMessageCounts();
  }, FLUSH_INTERVAL_MS);
}

function ensureBeforeUnload(): void {
  if (beforeUnloadBound) return;
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeunload', () => {
    void flushMessageCounts();
  });
  beforeUnloadBound = true;
}

function ensureIpcListener(): void {
  if (ipcListenerBound) return;
  if (typeof window === 'undefined') return;
  const onFlush = window.electronAPI?.analytics?.onFlushChatCounts;
  if (typeof onFlush !== 'function') return;
  onFlush(() => {
    void flushMessageCounts();
  });
  ipcListenerBound = true;
}

export function incrementMessageCount(adapter: string): void {
  const key = adapter || 'unknown';
  counts.set(key, (counts.get(key) ?? 0) + 1);
  ensureTimer();
  ensureBeforeUnload();
  ensureIpcListener();
}

export async function flushMessageCounts(): Promise<void> {
  if (counts.size === 0) return;
  const snapshot = Array.from(counts.entries());
  counts.clear();

  const track = (typeof window !== 'undefined')
    ? window.electronAPI?.analytics?.track
    : undefined;
  if (!track) return;

  await Promise.all(
    snapshot.map(([adapter, count]) =>
      track('feature:ai_chatbot_messages', { adapter, count }).catch(() => {})
    )
  );
}

/** Test-only reset. */
export function _reset(): void {
  counts.clear();
  if (flushTimer !== null) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  beforeUnloadBound = false;
  ipcListenerBound = false;
}

/** Test-only inspection. */
export function _getCounts(): ReadonlyMap<string, number> {
  return counts;
}
